import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  CloudOff,
  Sync,
  Storage,
  Delete,
  Update,
  CheckCircle,
} from '@mui/icons-material';
import { useOfflineStore } from '../../utils/offline/offline-state-manager';
import { formatBytes } from '../../utils/formatters';

export const OfflineSettings: React.FC = () => {
  const {
    offlineModeEnabled,
    backgroundSyncEnabled,
    notificationsEnabled,
    cacheSize,
    cacheLastUpdated,
    lastSyncTime,
    pendingSyncCount,
    conflictCount,
    updateAvailable,
    toggleOfflineMode,
    toggleBackgroundSync,
    toggleNotifications,
    clearOldCache,
    checkForUpdates,
    startSync,
  } = useOfflineStore();

  const [clearing, setClearing] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearResult, setClearResult] = useState<number | null>(null);

  const handleClearCache = async () => {
    setClearing(true);
    try {
      // Clear cache older than 30 days
      const deletedCount = await clearOldCache(30 * 24 * 60 * 60 * 1000);
      setClearResult(deletedCount);
      setShowClearDialog(false);
    } catch (error) {
      console.error('Failed to clear cache:', error);
    } finally {
      setClearing(false);
    }
  };

  const formatLastTime = (time: string | null) => {
    if (!time) return 'Never';

    const date = new Date(time);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
      <Typography variant="h4" gutterBottom style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CloudOff color="primary" />
        Offline Settings
      </Typography>

      {updateAvailable && (
        <Alert severity="info" style={{ marginBottom: 24 }} action={
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            Update Now
          </Button>
        }>
          A new version of Prismatic Forge is available. Update to get the latest features.
        </Alert>
      )}

      <Card style={{ marginBottom: 24 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Offline Features
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="Offline Mode"
                secondary="Work with cached content when offline"
              />
              <ListItemSecondaryAction>
                <FormControlLabel
                  control={
                    <Switch
                      checked={offlineModeEnabled}
                      onChange={toggleOfflineMode}
                      color="primary"
                    />
                  }
                  label=""
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemText
                primary="Background Sync"
                secondary="Automatically sync changes when connection is restored"
              />
              <ListItemSecondaryAction>
                <FormControlLabel
                  control={
                    <Switch
                      checked={backgroundSyncEnabled}
                      onChange={toggleBackgroundSync}
                      color="primary"
                      disabled={!offlineModeEnabled}
                    />
                  }
                  label=""
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemText
                primary="Push Notifications"
                secondary="Get notified about sync conflicts and updates"
              />
              <ListItemSecondaryAction>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationsEnabled}
                      onChange={toggleNotifications}
                      color="primary"
                    />
                  }
                  label=""
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Storage />
            Cache Management
          </Typography>

          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Typography variant="body2" color="text.secondary">
                Cache Size
              </Typography>
              <Typography variant="body2">
                {formatBytes(cacheSize)}
              </Typography>
            </div>
            <LinearProgress
              variant="determinate"
              value={Math.min((cacheSize / (500 * 1024 * 1024)) * 100, 100)}
              style={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" style={{ marginTop: 4, display: 'block' }}>
              Last updated: {formatLastTime(cacheLastUpdated)}
            </Typography>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <Button
              variant="outlined"
              startIcon={<Delete />}
              onClick={() => setShowClearDialog(true)}
              disabled={clearing}
            >
              Clear Old Cache
            </Button>
            <Button
              variant="outlined"
              startIcon={<Update />}
              onClick={checkForUpdates}
            >
              Check for Updates
            </Button>
          </div>

          {clearResult !== null && (
            <Alert severity="success" style={{ marginTop: 16 }} onClose={() => setClearResult(null)}>
              Cleared {clearResult} old cache entries
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sync />
            Sync Status
          </Typography>

          <List>
            <ListItem style={{ paddingLeft: 0, paddingRight: 0 }}>
              <ListItemText
                primary="Last Sync"
                secondary={formatLastTime(lastSyncTime)}
              />
              {lastSyncTime && <CheckCircle color="success" fontSize="small" />}
            </ListItem>

            <ListItem style={{ paddingLeft: 0, paddingRight: 0 }}>
              <ListItemText
                primary="Pending Changes"
                secondary={`${pendingSyncCount} changes waiting to sync`}
              />
              {pendingSyncCount > 0 && (
                <Chip
                  label={pendingSyncCount}
                  color="info"
                  size="small"
                />
              )}
            </ListItem>

            <ListItem style={{ paddingLeft: 0, paddingRight: 0 }}>
              <ListItemText
                primary="Conflicts"
                secondary={`${conflictCount} conflicts need resolution`}
              />
              {conflictCount > 0 && (
                <Chip
                  label={conflictCount}
                  color="warning"
                  size="small"
                />
              )}
            </ListItem>
          </List>

          {pendingSyncCount > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Sync />}
              onClick={startSync}
              fullWidth
              style={{ marginTop: 16 }}
            >
              Sync Now
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={showClearDialog}
        onClose={() => setShowClearDialog(false)}
      >
        <DialogTitle>Clear Old Cache?</DialogTitle>
        <DialogContent>
          <Alert severity="info" style={{ marginBottom: 16 }}>
            This will remove cached data older than 30 days that hasn't been modified offline.
            Your recent work and pending changes will be preserved.
          </Alert>
          <Typography>
            This action cannot be undone. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClearDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleClearCache}
            color="warning"
            variant="contained"
            disabled={clearing}
          >
            {clearing ? 'Clearing...' : 'Clear Cache'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
